import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IoAdd, IoClose } from "react-icons/io5";
import {
  updateBOQ,
  uploadBOQImage,
  deleteFile,
} from "../../../services/budgetService";
import SupplyApplyPills from "../../SupplyApplyPills";
import {
  getBoqDisplayImage,
  isPlaceholderBoqImage,
} from "../../../constants/boq";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";

const formatAed = (val) => {
  if (val == null || val === "") return null;
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-US");
};

const CurrencyValue = ({ amount, dashOnZero = false }) => {
  const isEmpty = amount == null || amount === "";
  const isZero = !isEmpty && Number(amount) === 0;
  if (isEmpty || (dashOnZero && isZero)) return "-";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {renderCurrencyIconColored("black-small")}
      {formatAed(amount)}
    </span>
  );
};

const ScopeBOQItemCard = ({
  boq,
  inclusionId,
  projectId,
  showUnitCostInput = false,
  isSiteEstimatesRoute = false,
  onBudgetUpdated,
  onEdit,
  onDelete,
  isSiteVisit = false,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const existingUnitCostRaw =
    boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
  const [unitCost, setUnitCost] = useState(
    existingUnitCostRaw != null ? String(existingUnitCostRaw) : ""
  );
  const [isSavingUnitCost, setIsSavingUnitCost] = useState(false);

  useEffect(() => {
    const next =
      boq.type === "Activities"
        ? boq.manpower_rate != null
          ? String(boq.manpower_rate)
          : ""
        : boq.unit_cost != null
        ? String(boq.unit_cost)
        : "";
    setUnitCost(next);
  }, [boq.type, boq.unit_cost, boq.manpower_rate]);

  const handleSaveUnitCost = async () => {
    const trimmed = String(unitCost ?? "").trim();
    if (isSiteEstimatesRoute) {
      if (trimmed === "") {
        toast.error("Please enter unit cost to proceed");
        return;
      }
      if (Number(trimmed) === 0) {
        toast.error("Enter a valid cost.");
        return;
      }
    } else if (trimmed !== "" && Number(trimmed) === 0) {
      toast.error("Enter a valid cost.");
      return;
    }
    if (!projectId) return;

    const boqType = boq.type || "Materials";
    const unitCostFallback =
      boqType === "Activities" ? boq.manpower_rate : boq.unit_cost;
    const unitCostValue =
      trimmed !== ""
        ? trimmed
        : unitCostFallback != null
        ? String(unitCostFallback)
        : null;

    const payload = {
      name: boq.item_name,
      boq_type: boqType,
      subcategory: boq.subcategory ?? "",
      supply_type: boq.supply_type,
      specification: boq.specification,
      ...(boqType === "Activities"
        ? {
            days: boq.days,
            manpower_rate: unitCostValue,
            qty: null,
            material_unit_price: null,
          }
        : {
            material_unit: boq.unit_of_measurement,
            qty: boq.quantity,
            material_unit_price: unitCostValue,
            days: null,
            manpower_rate: null,
          }),
    };

    try {
      setIsSavingUnitCost(true);
      await updateBOQ(projectId, boq.id, payload);
      toast.success("Budget Updated");
      onBudgetUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update budget");
    } finally {
      setIsSavingUnitCost(false);
    }
  };

  const [imageObj, setImageObj] = useState(() => getBoqDisplayImage(boq));
  useEffect(() => {
    setImageObj(getBoqDisplayImage(boq));
  }, [boq.image]);
  const [uploading, setUploading] = useState(false);
  const [deletingImg, setDeletingImg] = useState(false);
  const [imgHovered, setImgHovered] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !boq?.id) {
      if (e.target) e.target.value = "";
      return;
    }
    try {
      setUploading(true);
      const data = await uploadBOQImage(boq.id, file);
      const images = data?.images ?? data?.data ?? [];
      const first = images[0];
      const url = first?.image ?? first?.path;
      if (data?.success !== false && url) {
        toast.success("Image uploaded successfully");
        setImageObj({ id: first?.id ?? imageObj?.id ?? null, image: url });
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Error uploading BOQ image:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleImageDelete = async (e) => {
    e?.stopPropagation?.();
    if (!imageObj) return;
    if (isPlaceholderBoqImage(imageObj)) {
      setImageObj(null);
      onBudgetUpdated?.();
      return;
    }
    if (imageObj.id == null) return;
    try {
      setDeletingImg(true);
      const data = await deleteFile(imageObj.id);
      if (data?.success !== false) {
        toast.success("Image deleted successfully");
        setImageObj(null);
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (err) {
      console.error("Error deleting BOQ image:", err);
      toast.error("Failed to delete image");
    } finally {
      setDeletingImg(false);
    }
  };

  const specText =
    boq.specification != null && String(boq.specification).trim() !== ""
      ? String(boq.specification).trim()
      : "";
  const leadTimeText =
    boq.lead_time != null && String(boq.lead_time).trim() !== ""
      ? String(boq.lead_time).trim()
      : "";

  const unitCostDisplayRaw =
    boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
  const unitCostDisplay =
    unitCostDisplayRaw != null && unitCostDisplayRaw !== ""
      ? formatAed(unitCostDisplayRaw)
      : null;
  // Removed red border logic - always use default gray border
  // const isPriceMissing = !isSiteVisit && unitCostDisplay == null;
  const totalCostMissing =
    boq.total_cost == null ||
    boq.total_cost === "" ||
    Number(boq.total_cost) === 0;

  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "#fff",
        padding: 16,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        // Removed red border logic
        // border: isPriceMissing ? "1px solid #EF4444" : "1px solid #e5e7eb",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
      }}
    >
      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id={`scope-boq-card-img-up-${boq.id}`}
            disabled={uploading || isSiteVisit}
          />
          <div
            onMouseEnter={() => setImgHovered(true)}
            onMouseLeave={() => setImgHovered(false)}
            style={{
              position: "relative",
              width: 56,
              height: 56,
              borderRadius: 6,
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "#f3f4f6",
            }}
          >
            {imageObj?.image ? (
              <>
                <img
                  src={imageObj.image}
                  alt={boq.item_name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                  onClick={() => setLightboxOpen(true)}
                />
                {!isSiteVisit && imgHovered && (
                  <button
                    type="button"
                    onClick={handleImageDelete}
                    disabled={deletingImg}
                    title="Delete image"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      padding: 2,
                      background: "#ef4444",
                      color: "#fff",
                      border: "2px solid #fff",
                      borderRadius: 999,
                      cursor: deletingImg ? "not-allowed" : "pointer",
                      opacity: deletingImg ? 0.5 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    <IoClose style={{ width: 10, height: 10 }} />
                  </button>
                )}
                {lightboxOpen && (
                  <div
                    onClick={() => setLightboxOpen(false)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.8)",
                      zIndex: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={imageObj.image}
                      alt=""
                      style={{ maxWidth: "90%", maxHeight: "90%" }}
                    />
                  </div>
                )}
              </>
            ) : (
              <label
                htmlFor={`scope-boq-card-img-up-${boq.id}`}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isSiteVisit || uploading ? "not-allowed" : "pointer",
                  opacity: uploading ? 0.6 : 1,
                }}
                title={isSiteVisit ? undefined : "Upload image"}
              >
                <IoAdd style={{ width: 16, height: 16, color: "#6b7280" }} />
              </label>
            )}
          </div>
          <span
            style={{
              fontSize: 14,
              color: "#0D0D0E",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {boq.item_name || "-"}
          </span>
        </div>
        {boq.supply_type && <SupplyApplyPills supplyType={boq.supply_type} />}
      </div>

      {/* Row: BOQ Type + Material Unit */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, color: "#949DAA" }}>BOQ Type</span>
          <span style={{ fontSize: 14, color: "#0D0D0E" }}>{boq.type || "-"}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, color: "#949DAA" }}>Material Unit</span>
          <span style={{ fontSize: 14, color: "#0D0D0E" }}>
            {boq.unit_of_measurement || "-"}
          </span>
        </div>
      </div>

      {/* Quantity + Lead Time */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, color: "#949DAA" }}>
            {boq.type === "Materials" ? "Quantity" : "Days"}
          </span>
          <span style={{ fontSize: 14, color: "#0D0D0E" }}>
            {boq.type === "Materials" ? boq.quantity || "-" : boq.days || "-"}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, color: "#949DAA" }}>Lead Time</span>
          <span style={{ fontSize: 14, color: "#0D0D0E" }} title={leadTimeText || undefined}>
            {leadTimeText
              ? `${leadTimeText} day${leadTimeText === "1" ? "" : "s"}`
              : "-"}
          </span>
        </div>
      </div>

      {/* Unit Cost + Total Cost in same row */}
      {!isSiteVisit && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>
              {boq.type === "Activities" ? "Manpower Rate" : "Unit Cost"}
            </span>
            <span style={{ fontSize: 14, color: "#0D0D0E" }}>
              <CurrencyValue amount={unitCostDisplayRaw} />
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>Total Cost</span>
            <span style={{ fontSize: 14, color: "#0D0D0E" }}>
              <CurrencyValue amount={boq.total_cost} dashOnZero />
            </span>
          </div>
        </div>
      )}

      {/* Specification */}
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, width: "100%" }}>
        <span style={{ marginBottom: 4, fontSize: 14, color: "#949DAA" }}>Specification</span>
        <span
          style={{
            display: "block",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 14,
            color: "#0D0D0E",
          }}
          title={specText || undefined}
        >
          {specText || "—"}
        </span>
      </div>

      {/* Inline editable unit cost - COMMENTED OUT - Show label value instead */}
      {/* {!isSiteVisit && showUnitCostInput && (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>Unit Cost</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="text"
                inputMode="numeric"
                value={unitCost}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d+$/.test(v)) setUnitCost(v);
                }}
                placeholder="Enter Unit Cost"
                style={{
                  height: 44,
                  width: "50%",
                  borderRadius: 12,
                  border: "1px solid #d1d5db",
                  padding: "0 12px",
                  fontSize: 14,
                }}
              />
              <button
                type="button"
                onClick={handleSaveUnitCost}
                disabled={isSavingUnitCost}
                style={{
                  height: 44,
                  padding: "0 16px",
                  backgroundColor: "#4D6BF6",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: 12,
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isSavingUnitCost ? "not-allowed" : "pointer",
                  opacity: isSavingUnitCost ? 0.5 : 1,
                }}
              >
                {isSavingUnitCost ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Action buttons */}
      {/* {(onEdit || onDelete) && (
        <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, paddingTop: 4 }}>
          {onEdit && (
            <button
              onClick={onEdit}
              style={{
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 500,
                color: "#374151",
                backgroundColor: "#f3f4f6",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
              title="Edit BOQ item"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: 4,
                color: "#fff",
                backgroundColor: "#ef4444",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Delete BOQ item"
            >
              <IoClose style={{ width: 16, height: 16 }} />
            </button>
          )}
        </div>
      )} */}
    </div>
  );
};

export default ScopeBOQItemCard;
